import { S3_IMPORT_MAX_BYTES } from '../../catalog/diagnostics';
import { RCU_CMDTYPE, RCU_OTA, RCU_TOP_TYPE } from '../../catalog/protocol';
import type { Result } from '../../result';
import type { RcuDocument } from '../../wire';
import { nonEmptyText, validated } from './validate';

/**
 * Operations that change the device rather than an output: firmware updates,
 * coredumps, and importing a mapping file. All of them are disruptive or
 * destructive, and the console asks for confirmation before calling these.
 */

/** The compiled OTA URL, for the console to show before it is fetched. */
export const otaUrl = (version: string): string =>
  `${RCU_OTA.BASE_URL}${RCU_OTA.PREFIX}${version.trim()}${RCU_OTA.SUFFIX}`;

/**
 * Starts an update. There is no URL or build selector: the version is
 * substituted into the compiled URL, and several builds report the same version,
 * so the console shows the exact URL and the technician confirms it.
 */
export const startOta = (version: string): Result<RcuDocument> =>
  validated(
    {
      type: RCU_TOP_TYPE.COMMAND,
      data: { cmdtype: RCU_CMDTYPE.START_OTA, version: version.trim() },
    },
    [nonEmptyText('Version', version)],
  );

/** Reports whether a dump is stored, and what the last crash was. */
export const readCoredumpInfo = (): Result<RcuDocument> =>
  validated({ type: RCU_TOP_TYPE.COMMAND, data: { cmdtype: RCU_CMDTYPE.READ_COREDUMP_INFO } });

/**
 * Starts the chunked upload. It needs a stored dump and an MQTT connection; both
 * are reported in the failure ack rather than checked here, because the console
 * cannot know either for certain.
 */
export const downloadCoredump = (): Result<RcuDocument> =>
  validated({ type: RCU_TOP_TYPE.COMMAND, data: { cmdtype: RCU_CMDTYPE.DOWNLOAD_COREDUMP } });

/** Erases the stored dump. The flash copy stays until this runs or a new crash. */
export const eraseCoredump = (): Result<RcuDocument> =>
  validated({ type: RCU_TOP_TYPE.COMMAND, data: { cmdtype: RCU_CMDTYPE.ERASE_COREDUMP } });

/**
 * Imports a mapping file the RCU fetches itself. It is not transactional: entries
 * are applied as they are read, so a failure part way leaves what it already
 * wrote. The file must be under 32 KiB.
 */
export const importS3Mapping = (url: string): Result<RcuDocument> =>
  validated(
    {
      type: RCU_TOP_TYPE.COMMAND,
      data: { cmdtype: RCU_CMDTYPE.IMPORT_S3_MAPPING, url: url.trim() },
    },
    [nonEmptyText('URL', url)],
  );

/** The size limit the RCU applies to an imported file, for the console's copy. */
export const IMPORT_BYTE_LIMIT = S3_IMPORT_MAX_BYTES;
